import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConnectFormContainer from '../components/Containers/ConnectFormContainer';
import MainLogo from '../components/Titles/MainLogo';
import Subtitle from '../components/Texts/Subtitle';
import SmallText from '../components/Texts/SmallText';
import OutlineButton from '../components/Buttons/OutlineButton';
import { getApiUrl } from '../config/api';
import { useLanguage } from '../hooks/useLanguage';
import { logAuth } from '../utils/authDebug';

type MeResponse =
  | { success: true; data: { role?: string } }
  | { success: false; message?: string };

const SurfaceSelectorPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage, getPathWithLang } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string | null>(null);

  const { returnTo } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return { returnTo: params.get('returnTo') || '/' };
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiUrl('/api/v1/auth/me'), { credentials: 'include' });
        const json = (await res.json().catch(() => ({}))) as MeResponse;
        const meRole = (json as any)?.data?.role;
        logAuth('surface selector /auth/me', {
          ok: res.ok,
          status: res.status,
          success: (json as any)?.success,
          role: meRole,
        });
        if (!cancelled) setRole(typeof meRole === 'string' ? meRole : null);
      } catch {
        if (!cancelled) setRole(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loading) return;

    if (!role) {
      logAuth('surface selector: no role, back to login');
      navigate(getPathWithLang('/', currentLanguage), { replace: true });
      return;
    }

    if (role !== 'Admin') {
      const appOrigin = import.meta.env.VITE_APP_ORIGIN || 'http://app.localtest.me:3001';
      const targetUrl = `${appOrigin}${returnTo.startsWith('/') ? returnTo : '/'}`;
      logAuth('surface selector: non-admin redirect', { role, targetUrl });
      window.location.assign(targetUrl);
    }
  }, [loading, role, navigate, currentLanguage, getPathWithLang, returnTo]);

  const handleGo = (surface: 'app' | 'admin') => {
    const appOrigin = import.meta.env.VITE_APP_ORIGIN || 'http://app.localtest.me:3001';
    const adminOrigin = import.meta.env.VITE_ADMIN_ORIGIN || 'http://admin.localtest.me:3002';
    const targetOrigin = surface === 'admin' ? adminOrigin : appOrigin;
    const targetUrl = `${targetOrigin}${returnTo.startsWith('/') ? returnTo : '/'}`;
    logAuth('surface selector: user chose surface', { surface, targetUrl });
    window.location.assign(targetUrl);
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
      <ConnectFormContainer>
        <MainLogo />
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <Subtitle sx={{ mb: 1 }}>{t('surfaceSelector.title')}</Subtitle>
            <SmallText sx={{ mb: 2 }}>{t('surfaceSelector.subtitle')}</SmallText>

            <OutlineButton
              type="button"
              onClick={() => handleGo('app')}
              startIcon={<AppsOutlinedIcon />}
            >
              {t('surfaceSelector.app')}
            </OutlineButton>

            <OutlineButton
              type="button"
              onClick={() => handleGo('admin')}
              startIcon={<AdminPanelSettingsOutlinedIcon />}
            >
              {t('surfaceSelector.admin')}
            </OutlineButton>
          </>
        )}
      </ConnectFormContainer>
    </Box>
  );
};

export default SurfaceSelectorPage;
